import React, { useEffect, useState } from "react";
import { useGetDockProvisionalTripsQuery } from "../../api/vmsSlice";
import { useDockChangeRefetch, DOCK_FALLBACK_POLL_MS } from "./useDockRefresh";
import { lifecycleBadgeClass, lifecycleBadgeLabel, lifecycleSummary, formatPlantTime } from "./dockLifecycle";

const PROVISIONAL_PAGESIZE = 20;

/**
 * Trips that have left their first Bay and are either waiting for, or provisionally closed pending,
 * an optional second Bay leg.
 *
 * They have no Bay card and no queue row — the vehicle physically departed — so without this panel
 * they would be invisible on every screen until they closed. Rows come from the backend already
 * carrying their status and both deadlines; nothing here recomputes a deadline or mutates server
 * state on a timer. Refetching follows the existing socket invalidation with the shared fallback poll.
 */
export default function ProvisionalTrips({ plantId, enabled, onViewTimeline }) {
	const [pagination, setPagination] = useState({ plantId, page: 1 });
	const page = pagination.plantId === plantId ? pagination.page : 1;
	const setPage = (nextPage) => setPagination({ plantId, page: nextPage });
	const query = useGetDockProvisionalTripsQuery({ plantId, page_no: page, pagesize: PROVISIONAL_PAGESIZE },
		{ skip: !plantId || !enabled, pollingInterval: DOCK_FALLBACK_POLL_MS });
	useDockChangeRefetch(plantId, query.refetch);
	useEffect(() => {
		setPagination({ plantId, page: 1 });
	}, [plantId]);
	const currentCount = query.currentData?.count;
	useEffect(() => {
		if (!enabled || !plantId || query.currentData?.error || currentCount == null) return;
		const lastPage = Math.max(1, Math.ceil(currentCount / PROVISIONAL_PAGESIZE));
		if (page > lastPage) setPagination({ plantId, page: lastPage });
	}, [plantId, enabled, page, currentCount, query.currentData?.error]);
	const rows = query.data?.data || [];
	const count = query.data?.count || 0;
	const timezone = query.data?.timezone;
	const failed = Boolean(query.error || query.data?.error);
	// An empty panel is hidden rather than shown empty: on a plant that never returns to a second Bay
	// this list is empty all day, and a permanent "None" block is noise on a wall-mounted board.
	if (!failed && !rows.length && page === 1) return null;
	return (
		<section className="dock-queue-panel dock-provisional-panel" aria-label="Trips awaiting a second bay">
			<div className="dock-queue-panel-header dock-provisional-header">
				<h3>Awaiting second bay</h3>
				<span>{count} pending</span>
			</div>
			{failed && <p role="alert">Unable to load provisional trips. Please refresh.</p>}
			{rows.map((row) => {
				const lifecycle = row.dockLifecycle;
				const handleKeyDown = (event) => {
					if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onViewTimeline(row.tripId); }
				};
				return (
					<div
						role="button"
						tabIndex={0}
						aria-label={`View timeline for ${row.vehicleNumber || "Unknown vehicle"}`}
						className="dock-queue-row dock-provisional-row"
						key={row.tripId}
						onClick={() => onViewTimeline(row.tripId)}
						onKeyDown={handleKeyDown}
					>
						<div className="dock-provisional-vehicle">
							<span className="dock-queue-plate">{row.vehicleNumber || "Unknown vehicle"}</span>
							<span className={`dock-status ${lifecycleBadgeClass(lifecycle)}`}>{lifecycleBadgeLabel(lifecycle)}</span>
						</div>
						<div className="dock-provisional-details">
							<span className="dock-provisional-summary">{lifecycleSummary(lifecycle, timezone)}</span>
						{lifecycle?.firstBayDepartedAt && (
							<span className="dock-queue-meta dock-provisional-departed">Left bay {formatPlantTime(lifecycle.firstBayDepartedAt, timezone)}</span>
						)}
						</div>
						<span className="dock-provisional-action" aria-hidden="true">View timeline <span>→</span></span>
					</div>
				);
			})}
			{(page > 1 || count > PROVISIONAL_PAGESIZE) && (
				<div className="dock-detail-row">
					<button type="button" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
					<span>Page {page}</span>
					<button type="button" disabled={page * PROVISIONAL_PAGESIZE >= count} onClick={() => setPage(page + 1)}>Next</button>
				</div>
			)}
		</section>
	);
}
